import React, { useEffect, useState } from 'react';
import { Modal, Icon, Button } from 'antd';

/**
 * PhotoGalleryModal
 *
 * A reusable pop-up that displays the actual photos captured against a
 * reconciliation (AST) record and lets the user scroll through them.
 *
 * Data reliability note (see repo data-reliability rules):
 *  - It renders ONLY the photos passed in `photos`. It never invents,
 *    de-duplicates or substitutes images. An empty/absent list shows a
 *    clear "No photos" state rather than a placeholder image.
 *  - The total count shown is the exact length of the supplied array.
 *
 * Props:
 *  - visible  : boolean  -> controls modal visibility
 *  - photos   : array    -> [{ url: string }, ...] (as stored on the AST record)
 *  - title    : node     -> optional heading (e.g. asset code / AST no.)
 *  - onCancel : fn       -> close handler
 */
const PhotoGalleryModal = ({ visible, photos, title, onCancel }) => {
  const images = Array.isArray(photos) ? photos.filter(p => p && p.url) : [];
  const total = images.length;

  const [currentIndex, setCurrentIndex] = useState(0);

  // Reset to the first image whenever the modal is (re)opened or the
  // underlying photo set changes, so a previously viewed index does not
  // leak into a different record.
  useEffect(() => {
    if (visible) setCurrentIndex(0);
  }, [visible, photos]);

  // Clamp the index during render. The reset-to-0 effect above only runs
  // AFTER commit, so when the modal re-opens for a record with fewer photos
  // than the previously viewed one, the first render would otherwise use a
  // stale (out-of-range) index and read `.url` of an undefined image.
  const safeIndex = total > 0 ? Math.min(Math.max(currentIndex, 0), total - 1) : 0;

  const canPrev = safeIndex > 0;
  const canNext = safeIndex < total - 1;

  const handlePrev = () => setCurrentIndex(() => (safeIndex > 0 ? safeIndex - 1 : 0));
  const handleNext = () => setCurrentIndex(() => (safeIndex < total - 1 ? safeIndex + 1 : total - 1));

  // Keyboard navigation (left / right arrows) while the modal is open.
  useEffect(() => {
    if (!visible) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') handlePrev();
      else if (event.key === 'ArrowRight') handleNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, total, safeIndex]);

  const current = images[safeIndex];

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      centered
      width="60%"
      bodyStyle={{ padding: 0 }}
      title={
        <span>
          {title ? <span style={{ marginRight: 8 }}>{title}</span> : null}
          <span style={{ color: '#8c8c8c', fontWeight: 'normal' }}>
            {total > 0 ? `Photo ${safeIndex + 1} of ${total}` : 'No photos'}
          </span>
        </span>
      }
    >
      {(total === 0 || !current) ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c' }}>
          <Icon type="picture" style={{ fontSize: 32, display: 'block', marginBottom: 8 }} />
          No photos were captured for this record.
        </div>
      ) : (
        <div style={{ position: 'relative', background: '#000' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '65vh',
            }}
          >
            <img
              src={current.url}
              alt={`Photo ${safeIndex + 1} of ${total}`}
              style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />
          </div>

          {/* Prev / Next controls */}
          {canPrev && (
            <Button
              shape="circle"
              icon="left"
              onClick={handlePrev}
              style={{ position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)' }}
            />
          )}
          {canNext && (
            <Button
              shape="circle"
              icon="right"
              onClick={handleNext}
              style={{ position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)' }}
            />
          )}

          {/* Position counter overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              padding: '2px 12px',
              borderRadius: 12,
              fontSize: 12,
            }}
          >
            {safeIndex + 1} / {total}
          </div>

          <a
            href={current.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              padding: '2px 10px',
              borderRadius: 12,
              fontSize: 12,
            }}
          >
            <Icon type="fullscreen" /> Open
          </a>
        </div>
      )}

      {/* Thumbnail strip for quick scrolling */}
      {total > 1 && (
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: 8,
            overflowX: 'auto',
            background: '#141414',
          }}
        >
          {images.map((img, ix) => (
            <img
              key={img.url}
              src={img.url}
              alt={`Thumbnail ${ix + 1}`}
              onClick={() => setCurrentIndex(ix)}
              style={{
                height: 56,
                width: 56,
                objectFit: 'cover',
                cursor: 'pointer',
                borderRadius: 4,
                border: ix === safeIndex ? '2px solid #1890ff' : '2px solid transparent',
                opacity: ix === safeIndex ? 1 : 0.7,
                flex: '0 0 auto',
              }}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};

export default PhotoGalleryModal;
